import { computed, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { AccountService, DownloadDocumentService } from '@okcargo-apps/shared-application-ionic';
import {
  EFreightForCarrierStatus,
  EStopStatus,
  EVehicleType,
  getEmptyFreightBasicForCarrier,
  getEmptyFreightDetailedForCarrier,
  ICoords,
  IDigitizedDeliveryNote,
  IFreightBasicForCarrier,
  IFreightDetailedForCarrier,
  IFreightVehicle,
  IStopDetailedForCarrier,
  IStopGoodsInfo,
  UUID,
} from '@okcargo-apps/shared-domain';
import { IAccount } from '@okcargo-apps/shared-domain-ionic';
import {
  transformToIFreightBasicForCarrier,
  transformToIFreightDetailForCarrier,
} from '@okcargo-apps/shared-infrastructure';
import { FreightCommandService, FreightQueryService } from '@okcargo-apps/shared-infrastructure-ionic';
import {
  ArriveFreightStopCommand,
  DriverPayload,
  RequestPayInAdvanceCommand,
  UploadFreightDocumentCommand,
  VehiclePayload,
  VerifyFreightStopGoodsInfoCommand,
} from '@okcargo/command-processor';
import {
  CriteriaPayload,
  DocumentMetadataFieldNames,
  DocumentMetadataResponse,
  FilterCombinator,
  FilterComparator,
  FilterPayload,
  FindFreightQuery,
  FreightBasicForCarrierResponse,
  FreightFieldNames,
  FreightStatus,
  OrderPayload,
  OrderType,
  SearchDocumentMetadataQuery,
  SearchFreightsWithPendingDocumentationQuery,
  SearchInProgressFreightsQuery,
  SearchNotInProgressFreightsQuery,
} from '@okcargo/query-processor';
import { BehaviorSubject, forkJoin, from, map, Observable, of, switchMap, tap } from 'rxjs';
import { IUser } from '../../user/domain/user.model';
import { IVehicle } from '../../vehicle/domain/vehicle.model';

enum FreightType {
  IN_PROGRESS = 'IN_PROGRESS',
  FINISHED = 'FINISHED',
}

interface FreightFilters {
  driverId?: string;
  reference?: string;
  hasPendingDocumentation?: boolean;
}

// interface PaginationState {
//   currentPage: number;
//   hasMoreData: boolean;
//   isLoadingMore: boolean;
//   pageStatus: Map<number, PageStatus>;
//   lastRefreshTimestamp: number;
// }

@Injectable({
  providedIn: 'root',
})
export class FreightService {
  private readonly freightQueryService = inject(FreightQueryService);
  private readonly freightCommandService = inject(FreightCommandService);
  private readonly downloadDocumentService = inject(DownloadDocumentService);
  private readonly accountService = inject(AccountService);

  private readonly DEFAULT_PAGE: number = 0;
  // private readonly DEFAULT_SIZE: number = 50;
  private readonly DEFAULT_SIZE: number = 200;
  private readonly MAX_PAGES_LOADED = 3;

  private account!: IAccount;

  private readonly _lastRefreshFreightDetailTimestamp = signal<number>(Date.now());

  // TODO añadir filtros por: referencia, conductor, hay documentación pendiente, fechas (este más adelante)
  // TODO mejorar la estrategia de refresco: temporizador independiente para cada query, refresco con paginación

  // TODO refactor and use the existing logic with pending documentation
  private readonly _freightstWithPendingDocumentation$ = new BehaviorSubject<IFreightBasicForCarrier[]>([]);
  private readonly _freightstWithPendingDeliveryNoteInPaper$ = new BehaviorSubject<IFreightBasicForCarrier[]>([]);
  private readonly _freightstWithPendingDocumentationLoading = signal(true);
  public freightstWithPendingDocumentationLoading = this._freightstWithPendingDocumentationLoading.asReadonly();
  // END TODO ANOTATION

  // TODO add queryExecutorMap as private (see serarchFreights())

  // TODO migrate BehaviorSubject to Signal?

  // Data structures using Record Data to force all enum values to be used
  // It is equivalent to declaring as follows but ensuring that all elements of the enum are used (otherwise,
  // it does not compile)
  // private readonly _freightData = {
  //   [FreightType.IN_PROGRESS]: signal<IFreightBasic[]>([]),
  //   [FreightType.FINISHED]: signal<IFreightBasic[]>([]),
  // } as const;
  private readonly _freightData: Record<FreightType, WritableSignal<IFreightBasicForCarrier[]>> = {
    [FreightType.IN_PROGRESS]: signal<IFreightBasicForCarrier[]>([]),
    [FreightType.FINISHED]: signal<IFreightBasicForCarrier[]>([]),
  };

  private readonly _loading: Record<FreightType, WritableSignal<boolean>> = {
    [FreightType.IN_PROGRESS]: signal(true),
    [FreightType.FINISHED]: signal(true),
  };

  private readonly _hasMoreDataBelow: Record<FreightType, WritableSignal<boolean>> = {
    [FreightType.IN_PROGRESS]: signal(true),
    [FreightType.FINISHED]: signal(true),
  };

  private readonly _isLoadingMore: Record<FreightType, WritableSignal<boolean>> = {
    [FreightType.IN_PROGRESS]: signal(false),
    [FreightType.FINISHED]: signal(false),
  };

  private readonly _pageData: Record<FreightType, WritableSignal<Map<number, IFreightBasicForCarrier[]>>> = {
    [FreightType.IN_PROGRESS]: signal<Map<number, IFreightBasicForCarrier[]>>(new Map()),
    [FreightType.FINISHED]: signal<Map<number, IFreightBasicForCarrier[]>>(new Map()),
  };

  // TODO only for debug, remove it
  private readonly _loadedPages: Record<FreightType, WritableSignal<number[]>> = {
    [FreightType.IN_PROGRESS]: signal<number[]>([]),
    [FreightType.FINISHED]: signal<number[]>([]),
  };

  private readonly _activeFilters: Record<FreightType, WritableSignal<FreightFilters>> = {
    [FreightType.IN_PROGRESS]: signal<FreightFilters>({}),
    [FreightType.FINISHED]: signal<FreightFilters>({}),
  };

  private readonly _hasMoreDataAbove: Record<FreightType, Signal<boolean>> = {
    [FreightType.IN_PROGRESS]: computed(() => {
      const loadedPages = this._loadedPages[FreightType.IN_PROGRESS]();
      return loadedPages.length === 0 ? false : Math.min(...loadedPages) > 0;
    }),
    [FreightType.FINISHED]: computed(() => {
      const loadedPages = this._loadedPages[FreightType.FINISHED]();
      return loadedPages.length === 0 ? false : Math.min(...loadedPages) > 0;
    }),
  };

  public readonly freightsInProgressLoading = this._loading[FreightType.IN_PROGRESS].asReadonly();
  public readonly freightsFinishedLoading = this._loading[FreightType.FINISHED].asReadonly();
  public readonly hasMoreDataBelowInProgress = this._hasMoreDataBelow[FreightType.IN_PROGRESS].asReadonly();
  public readonly hasMoreDataBelowFinished = this._hasMoreDataBelow[FreightType.FINISHED].asReadonly();
  public readonly hasMoreDataAboveInProgress = this._hasMoreDataAbove[FreightType.IN_PROGRESS]; // is already computed()
  public readonly hasMoreDataAboveFinished = this._hasMoreDataAbove[FreightType.FINISHED]; // is already computed()
  public readonly isLoadingMoreInProgress = this._isLoadingMore[FreightType.IN_PROGRESS].asReadonly();
  public readonly isLoadingMoreFinished = this._isLoadingMore[FreightType.FINISHED].asReadonly();
  public readonly pageDataInProgress = this._pageData[FreightType.IN_PROGRESS].asReadonly();
  public readonly pageDataFinished = this._pageData[FreightType.FINISHED].asReadonly();

  // TODO refactor to signals???
  private readonly _freightsInProgress$ = new BehaviorSubject<IFreightBasicForCarrier[]>([]);
  private readonly _freightsFinished$ = new BehaviorSubject<IFreightBasicForCarrier[]>([]);

  // TODO only for debug, remove it
  public readonly currentPagesFinished = this._loadedPages[FreightType.FINISHED].asReadonly();

  constructor() {
    this.accountService.account$.subscribe((account) => {
      if (account) {
        this.account = account;
      }
    });

    // // only for debug
    // effect(() => {
    //   console.log(
    //     '[FreightService] constructor effect() this._loadedPagesFinished()',
    //     this._loadedPages[FreightType.FINISHED](),
    //   );
    // });
  }

  // TODO If these observables become signals, expose with asReadonly, no getter needed.
  get freightsInProgress$(): Observable<IFreightBasicForCarrier[]> {
    return this._freightsInProgress$.asObservable();
  }

  get freightsFinished$(): Observable<IFreightBasicForCarrier[]> {
    return this._freightsFinished$.asObservable();
  }

  get freightstWithPendingDocumentation$(): Observable<IFreightBasicForCarrier[]> {
    return this._freightstWithPendingDocumentation$.asObservable();
  }

  get freightstWithDeliveryNoteInPaper$(): Observable<IFreightBasicForCarrier[]> {
    return this._freightstWithPendingDeliveryNoteInPaper$.asObservable();
  }

  // TODO use it in components template (freightDetail)
  public get lastRefreshFreightDetailTimestamp(): number {
    return this._lastRefreshFreightDetailTimestamp();
  }

  // TODO review, update only active pages and not refresh all
  public refreshFreightsInprogress(): void {
    const filters = this._activeFilters[FreightType.IN_PROGRESS]();
    this.resetPaginationState(FreightType.IN_PROGRESS);
    this.searchFreights(0, FreightType.IN_PROGRESS, true, false, filters);
  }

  // TODO review, update only active pages and not refresh all
  public refreshFreightsFinished(): void {
    const filters = this._activeFilters[FreightType.FINISHED]();
    this.resetPaginationState(FreightType.FINISHED);
    this.searchFreights(0, FreightType.FINISHED, true, false, filters);
  }

  // TODO refactor or remove when we unify query for pending documentation
  public refreshFreightstWithPendingDocumentation(): void {
    this.searchAllFreightsWithPendingDocumentation();
  }

  public refreshFreightDetail(): void {
    // Trigger/modifies the signal's content so that the component (they has
    // the freight id) makes the corresponding call in the constructor's effect()
    this._lastRefreshFreightDetailTimestamp.set(Date.now());
  }

  // TODO use in component
  public loadNextPageInProgress(): void {
    this.loadNextPage(FreightType.IN_PROGRESS);
  }

  public loadNextPageFinished(): void {
    this.loadNextPage(FreightType.FINISHED);
  }

  public loadPreviousPageInProgress(): void {
    this.loadPreviousPage(FreightType.IN_PROGRESS);
  }

  public loadPreviousPageFinished(): void {
    this.loadPreviousPage(FreightType.FINISHED);
  }

  // TODO new, filters...
  public applyFilters(filters: FreightFilters, type: FreightType): void {
    this._activeFilters[type].set(filters);
    this.resetPaginationState(type);
    this.searchFreights(0, type, true, false, filters);
  }

  public clearFilters(type: FreightType): void {
    this._activeFilters[type].set({});
    this.resetPaginationState(type);
    this.searchFreights(0, type, true, false, {});
  }

  private loadNextPage(type: FreightType): void {
    const hasMoreBelow = this._hasMoreDataBelow[type]();
    const isLoading = this._isLoadingMore[type]();

    if (!hasMoreBelow || isLoading) {
      return;
    }

    this._isLoadingMore[type].set(true);

    const loadedPages = this._loadedPages[type]();
    const currentLastPage = loadedPages.length > 0 ? Math.max(...loadedPages) : 0;
    const nextPage = currentLastPage + 1;

    // TODO debug only, remove
    // console.log(`[FreightService] loadNextPage() ${type} nextPage`, nextPage);

    const filters = this._activeFilters[type]();
    this.searchFreights(nextPage, type, false, false, filters);
  }

  private loadPreviousPage(type: FreightType): void {
    const hasMoreAbove = this._hasMoreDataAbove[type]();
    const isLoading = this._isLoadingMore[type]();

    if (!hasMoreAbove || isLoading) {
      return;
    }

    this._isLoadingMore[type].set(true);

    const loadedPages = this._loadedPages[type]();
    const currentFirstPage = Math.min(...loadedPages);
    const previousPage = currentFirstPage - 1;

    // TODO debug only, remove
    // console.log(`[FreightService] loadPreviousPage() ${type} previousPage`, previousPage);

    const filters = this._activeFilters[type]();
    this.searchFreights(previousPage, type, false, true, filters);
  }

  private searchFreights(
    pageIndex = 0,
    type: FreightType,
    resetData = false,
    isLoadingAbove = false,
    filters: FreightFilters = {},
  ): void {
    const query = this.buildFreightQuery(pageIndex, type, filters);

    // TODO move this const as a private (see top)
    const queryExecutorMap: Record<FreightType, () => Promise<FreightBasicForCarrierResponse[]>> = {
      [FreightType.IN_PROGRESS]: () => this.freightQueryService.searchInProgressFreightsForCarriers(query),
      [FreightType.FINISHED]: () => this.freightQueryService.searchFinishedFreightsForCarriers(query),
    };

    from(queryExecutorMap[type]())
      .pipe(
        map((freights) =>
          freights.map((freightBasicResponse): IFreightBasicForCarrier => {
            const freight: IFreightBasicForCarrier = getEmptyFreightBasicForCarrier();
            return transformToIFreightBasicForCarrier(freightBasicResponse, freight);
          }),
        ),
        // TODO review, only freights that can be selected by a user should arrive
        // Solo revisar en caso de IN_PROGRESS
        tap((freights) => {
          if (type === FreightType.IN_PROGRESS) {
            const valid = freights.filter((f) => f.status !== EFreightForCarrierStatus.FINISHED);
            const invalid = freights.filter((f) => f.status === EFreightForCarrierStatus.FINISHED);

            // console.log('Freights que cumplen condición (status !== FINISHED):', valid);
            // console.log('Freights que NO cumplen condición (status === FINISHED):', invalid);
          }
        }),
        // TODO review in backend
        // Filter finished freights for IN_PROGRESS type
        map((freights) =>
          type === FreightType.IN_PROGRESS
            ? freights.filter((freight) => freight.status !== EFreightForCarrierStatus.FINISHED)
            : freights,
        ),
      )
      .subscribe((transformedFreights) => {
        this.handlePaginatedResults(transformedFreights, pageIndex, type, resetData, isLoadingAbove);
      });
  }

  private buildFreightQuery(
    pageIndex: number,
    type: FreightType,
    userFilters: FreightFilters = {},
  ): SearchInProgressFreightsQuery | SearchNotInProgressFreightsQuery {
    const baseFilters = this.getBaseFilters();
    const userFilterArray = this.transformUserFilters(userFilters);
    const combinedFilters = [...baseFilters, ...userFilterArray];

    if (type === FreightType.IN_PROGRESS) {
      combinedFilters.push({
        // combinator: FilterCombinator.AND,
        combinator: FilterCombinator.NONE,
        field: FreightFieldNames.STATUS,
        comparator: FilterComparator.NOT_IN,
        value:
          EFreightForCarrierStatus.FINISHED +
          '|' +
          EFreightForCarrierStatus.CANCELLED +
          '|' +
          FreightStatus.ABORTED +
          '|' +
          FreightStatus.PLANNING,
        group: null,
      } as unknown as FilterPayload);

      // combinedFilters.push({
      //   combinator: FilterCombinator.AND,
      //   field: FreightFieldNames.STATUS,
      //   comparator: FilterComparator.NEQ,
      //   value: EFreightStatus.CANCELLED,
      //   group: null,
      // } as unknown as FilterPayload);
    }

    const query = {
      criteriaPayload: {
        order: this.getOrderConfig(type),
        filters: combinedFilters,
        pageIndex: pageIndex,
        pageSize: this.DEFAULT_SIZE,
      } as unknown as CriteriaPayload,
    };

    return query as SearchInProgressFreightsQuery | SearchNotInProgressFreightsQuery;
  }

  private getBaseFilters(): FilterPayload[] {
    // only freights assigned to company
    return [
      // {
      //   combinator: FilterCombinator.NONE,
      //   field: FreightFieldNames.CARRIER_ID,
      //   comparator: FilterComparator.EQ,
      //   value: this.account.company.id,
      //   group: null,
      // } as unknown as FilterPayload,
    ];
  }

  private transformUserFilters(userFilters: FreightFilters): FilterPayload[] {
    const filters: FilterPayload[] = [];

    // TODO review field name
    if (userFilters.driverId) {
      filters.push({
        combinator: FilterCombinator.NONE,
        field: FreightFieldNames.DRIVER_CONTACT_ID,
        comparator: FilterComparator.EQ,
        value: userFilters.driverId,
        // group: null,
      } as FilterPayload);
    }

    // TODO review field name
    if (userFilters.reference) {
      filters.push({
        combinator: FilterCombinator.NONE,
        field: FreightFieldNames.SHIPPER_FREIGHT_M2M_REFERENCE,
        comparator: FilterComparator.LIKE,
        value: userFilters.reference,
        // group: null,
      } as FilterPayload);
    }

    // TODO review field name or query filter
    // if (userFilters.hasPendingDocumentation !== undefined) {
    //   filters.push({
    //     combinator: FilterCombinator.NONE,
    //     field: FreightFieldNames.HAS_PENDING_DOCUMENTATION,
    //     comparator: FilterComparator.EQ,
    //     value: userFilters.hasPendingDocumentation,
    //     // group: null,
    //   } as FilterPayload);
    // }

    return filters;
  }

  // TODO refactor this, a property of freight type query
  private getOrderConfig(type: FreightType): OrderPayload[] {
    const orderType = type === FreightType.IN_PROGRESS ? OrderType.ASC : OrderType.DESC;
    return [
      {
        field: FreightFieldNames.LATEST_STARTING_AT,
        type: orderType,
      } as OrderPayload,
      // quickfix: Added a second sort order to avoid pagination issues
      // (if multiple freights have the same STARTING_AT and are on the edge
      // of the pagination then they may appear repeatedly on different pages
      // or not appear on either page)
      {
        field: FreightFieldNames.ID,
        type: OrderType.DESC,
      } as OrderPayload,
    ];
  }

  private handlePaginatedResults(
    transformedFreights: IFreightBasicForCarrier[],
    pageIndex: number,
    type: FreightType,
    resetData = false,
    isLoadingAbove = false,
  ): void {
    const hasMoreBelowData = transformedFreights.length === this.DEFAULT_SIZE;

    // TODO debug only, remove
    // console.log('[FreightService] handlePaginatedResults() hasMoreBelowData', hasMoreBelowData);
    // console.log('[FreightService] handlePaginatedResults() page', pageIndex);
    // console.log('[FreightService] handlePaginatedResults() isLoadingAbove', isLoadingAbove);

    if (!isLoadingAbove) {
      this._hasMoreDataBelow[type].set(hasMoreBelowData);
    } else {
      this._hasMoreDataBelow[type].set(true);
    }

    this._loading[type].set(false);
    this.updatePageData(pageIndex, transformedFreights, resetData, type, isLoadingAbove);
  }

  private updatePageData(
    pageIndex: number,
    newData: IFreightBasicForCarrier[],
    resetData: boolean,
    type: FreightType,
    isLoadingAbove = false,
  ): void {
    const loadedPagesSignal = this._loadedPages[type];
    const pageDataSignal = this._pageData[type];

    // TODO debug only, remove
    // console.log('[FreightService] updatePageData() type', type);
    // console.log('[FreightService] updatePageData() resetData', resetData);
    // console.log('[FreightService] updatePageData() isLoadingAbove', isLoadingAbove);
    // console.log('[FreightService] updatePageData() newData', newData);

    if (resetData) {
      const newPageData = new Map();
      newPageData.set(pageIndex, newData);
      const newLoadedPages = [pageIndex];

      pageDataSignal.set(newPageData);
      loadedPagesSignal.set(newLoadedPages);
      this._freightData[type].set(newData);
      this.updateBehaviorSubject(type, newData);
      return;
    }

    // Add new page
    const updatedPageData = new Map(this._pageData[type]());
    updatedPageData.set(pageIndex, newData);

    const updatedLoadedPages = [...this._loadedPages[type]()];
    if (!updatedLoadedPages.includes(pageIndex)) {
      updatedLoadedPages.push(pageIndex);
      updatedLoadedPages.sort((a, b) => a - b);
    }

    // Keep only last MAX_PAGES_LOADED pages
    const [finalPageData, finalLoadedPages] = this.managePageLimit(updatedLoadedPages, updatedPageData, isLoadingAbove);

    console.log('[FreightService] updatePageData() updatedPageData - DESPUÉS de eliminar', finalPageData);

    pageDataSignal.set(finalPageData);
    loadedPagesSignal.set(finalLoadedPages);

    const combinedData = this.combinePagesData(finalLoadedPages, finalPageData);

    this._freightData[type].set(combinedData);
    this.updateBehaviorSubject(type, combinedData);
    this._isLoadingMore[type].set(false);
  }

  private managePageLimit(
    updatedLoadedPages: number[],
    updatedPageData: Map<number, IFreightBasicForCarrier[]>,
    isLoadingAbove: boolean,
  ): [Map<number, IFreightBasicForCarrier[]>, number[]] {
    if (updatedLoadedPages.length > this.MAX_PAGES_LOADED) {
      if (isLoadingAbove) {
        // Loading upwards, remove pages from the end
        const pagesToRemove = updatedLoadedPages.slice(-1);
        pagesToRemove.forEach((page) => {
          updatedPageData.delete(page);
        });
        updatedLoadedPages = updatedLoadedPages.slice(0, this.MAX_PAGES_LOADED);
      } else {
        // Loading downwards, remove pages from the beginning
        const pagesToRemove = updatedLoadedPages.slice(0, updatedLoadedPages.length - this.MAX_PAGES_LOADED);
        pagesToRemove.forEach((page) => {
          updatedPageData.delete(page);
        });
        updatedLoadedPages = updatedLoadedPages.slice(-this.MAX_PAGES_LOADED);
      }
    }
    return [updatedPageData, updatedLoadedPages];
  }

  private combinePagesData(
    updatedLoadedPages: number[],
    updatedPageData: Map<number, IFreightBasicForCarrier[]>,
  ): IFreightBasicForCarrier[] {
    const combinedData: IFreightBasicForCarrier[] = [];
    updatedLoadedPages.forEach((page) => {
      const pageDataArray = updatedPageData.get(page);
      if (pageDataArray) {
        combinedData.push(...pageDataArray);
      }
    });

    // Remove duplicates
    // return Array.from(new Map(combinedData.map((freight) => [freight.id, freight])).values());
    return combinedData;
  }

  private updateBehaviorSubject(type: FreightType, data: IFreightBasicForCarrier[]): void {
    if (type === FreightType.IN_PROGRESS) {
      this._freightsInProgress$.next(data);
    } else {
      this._freightsFinished$.next(data);
    }
  }

  private resetPaginationState(type: FreightType): void {
    this._hasMoreDataBelow[type].set(true);
    this._isLoadingMore[type].set(false);
    this._loading[type].set(true);
    this._loadedPages[type].set([]);
    this._pageData[type].set(new Map());
  }

  // TODO use new system in this query???

  private searchAllFreightsWithPendingDocumentation(): void {
    const query = {
      criteriaPayload: {
        order: [
          {
            field: FreightFieldNames.LATEST_STARTING_AT,
            type: OrderType.ASC,
          } as OrderPayload,
        ],
        filters: [],
        pageIndex: this.DEFAULT_PAGE,
        pageSize: 200,
      } as unknown as CriteriaPayload,
    } as SearchFreightsWithPendingDocumentationQuery;

    const afterDate = new Date(2024, 2, 1); // 1 de marzo de 2024

    from(this.freightQueryService.searchFreightsForCarriersWithPendingDocumentation(query))
      .pipe(
        map((freights): IFreightBasicForCarrier[] =>
          freights
            // .filter(
            //   (freightBasicResponse) =>
            //     freightBasicResponse.shipperId === 'f1ad8da7-e84d-4d7e-9363-000000000050' && // delete when the shipper is not OkCargo
            //     isAfter(freightBasicResponse.finishingAt, afterDate), // remove when is before 1 march 2024
            //   // TODO review above, probably migrate to a single query with filters to get freights and not use SearchFreightsWithPendingDocumentationQuery
            // )
            .map((freightBasicResponse): IFreightBasicForCarrier => {
              const freight: IFreightBasicForCarrier = getEmptyFreightBasicForCarrier();
              return transformToIFreightBasicForCarrier(freightBasicResponse, freight);
            }),
        ),
        map((transformedFreights) => {
          const freightsWithPendingDeliveryNoteInPaper = transformedFreights.filter(
            (freight) => freight.deliveryNoteInPaperReceived === false,
          );
          const freightsWithPendingDigitizedDocumentation = transformedFreights.filter(
            (freight) => freight.hasPendingDoc === true,
          );
          return {
            freightsWithPendingDeliveryNoteInPaper,
            freightsWithPendingDigitizedDocumentation,
          };
        }),
      )
      .subscribe(({ freightsWithPendingDeliveryNoteInPaper, freightsWithPendingDigitizedDocumentation }) => {
        this._freightstWithPendingDeliveryNoteInPaper$.next(freightsWithPendingDeliveryNoteInPaper);
        this._freightstWithPendingDocumentation$.next(freightsWithPendingDigitizedDocumentation);
        this._freightstWithPendingDocumentationLoading.set(false);
      });
  }

  public getFreightDetail$(id: string): Observable<IFreightDetailedForCarrier> {
    const query = {
      id: id,
    } as FindFreightQuery;

    return from(this.freightQueryService.findFreight(query)).pipe(
      switchMap((freightDetailedResponse): Observable<IFreightDetailedForCarrier> => {
        const freight: IFreightDetailedForCarrier = getEmptyFreightDetailedForCarrier();
        const transformedFreight = transformToIFreightDetailForCarrier(freightDetailedResponse, freight);

        return this.searchFreightDetailDocuments$(freightDetailedResponse.id).pipe(
          map((documents) => {
            // TODO review this, mapper necessary??? probably yes
            const deliveryNotes = documents.filter((document) => document.type === 'DELIVERY_NOTE');
            transformedFreight.digitizedDeliveryNotes = deliveryNotes as unknown as IDigitizedDeliveryNote[];
            return transformedFreight;
          }),
        );
      }),
    );
  }

  private searchFreightDetailDocuments$(freightId: string): Observable<DocumentMetadataResponse[]> {
    const query = {
      criteriaPayload: {
        order: [],
        filters: [
          {
            field: DocumentMetadataFieldNames.AGGREGATE_ID,
            comparator: FilterComparator.EQ,
            value: freightId,
            combinator: FilterCombinator.NONE,
          } as FilterPayload,
        ], // FilterPayload[]; create a filter by history or in-progress
        pageIndex: 0,
        pageSize: 30,
      } as unknown as CriteriaPayload,
    } as SearchDocumentMetadataQuery;

    return from(this.freightQueryService.searchDocumentMetadata(query)).pipe(
      switchMap((documents: DocumentMetadataResponse[]) => {
        if (documents.length === 0) {
          return of([]);
        }

        const documentObservables = documents.map((document) => {
          const url = this.downloadDocumentService.getDownloadDocumentURL(document);
          return of({ ...document, url });
        });
        return forkJoin(documentObservables);
      }),
    );
  }

  // In old app:  async markDepartureDateTime(stop: FreightStopResponse) { ...
  public async modifyStopStatus(
    stop: IStopDetailedForCarrier,
    freight: IFreightDetailedForCarrier,
    nextStopStatus: EStopStatus,
    coords?: ICoords,
  ): Promise<void> {
    // const point = this.getPoint(); // TODO not in use in old app
    // old markDepartureDateTime() and markArrivalDateTime()

    const trailerVehicle: IFreightVehicle | null =
      freight.vehicles?.find((vehicle) => vehicle.type === EVehicleType.TRAILER) || null;
    const motorizedVehicle: IFreightVehicle | null =
      freight.vehicles?.find((vehicle) => vehicle.type !== EVehicleType.TRAILER) || null;

    const command = {
      freightId: freight.id,
      stopId: stop.id,
      driverPayload: {} as DriverPayload, // TODO for now it is necessary to send it even if it is empty
      // TODO DriverPayload Set info in server
      // TODO add driverAppPointPayload
    } as ArriveFreightStopCommand; // TODO check this, same interface in both cases? It comes from old app...

    if (coords) {
      command.driverAppPointPayload = coords;
    }

    if (motorizedVehicle) {
      command.motorizedVehiclePayload = {
        id: motorizedVehicle.id,
        plateNumber: motorizedVehicle.plate,
      } as unknown as VehiclePayload;
    }
    if (trailerVehicle) {
      command.trailerVehiclePayload = {
        id: trailerVehicle.id,
        plateNumber: trailerVehicle.plate,
      } as unknown as VehiclePayload;
    }

    if (nextStopStatus === EStopStatus.STARTED) {
      this.freightCommandService.arriveToFreightStop(command).then(
        () => {
          // TODO google analytics
        },
        async (error: any) => {
          // TODO implement this
          console.warn(error);
        },
      );
    } else if (nextStopStatus === EStopStatus.FINISHED) {
      this.freightCommandService.departFromFreightStop(command).then(
        () => {
          // TODO google analytics
        },
        async (error: any) => {
          // TODO implement this
          console.warn(error);
        },
      );
    }
  }

  // In old app:  async sendData() { ... in freight-data.component.ts
  public async sendStopGoodsInfo(command: IStopGoodsInfo): Promise<void> {
    this.freightCommandService.verifyFreightStopGoodsInfo(command as VerifyFreightStopGoodsInfoCommand).then(
      () => {
        // TODO review this
      },
      async (error: any) => {
        // TODO implement this
        console.warn(error);
      },
    );
  }

  public async uploadFreightDeliveryNote(fileInput: File, stopId: string, freightId: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = async (readerEvent: ProgressEvent<FileReader>): Promise<void> => {
        const fileData = (readerEvent.target as FileReader).result as string;
        const fileType = fileInput.type;
        const fileName = fileInput.name;
        const extension = fileName.substring(fileName.lastIndexOf('.'));
        // const extension = '.pdf'; // TODO always pdf? or maybe convert to PDF
        const base64Data = fileData.substring(fileData.indexOf(',') + 1);
        const uploadFileName = 'order_' + this.getFileFormattedDate(new Date()) + extension;

        const command: UploadFreightDocumentCommand = {
          data: base64Data,
          contentType: fileType, // 'application/pdf' ¿?¿?¿?
          description: '',
          documentId: crypto.randomUUID(),
          freightId: freightId,
          name: uploadFileName,
          type: 'DELIVERY_NOTE', // DocumentType.DELIVERY_NOTE, // TODO: enum?
          freightStopId: stopId,
        } as unknown as UploadFreightDocumentCommand;

        try {
          await this.freightCommandService.uploadFreightDocument(command);
          resolve(fileName);
        } catch (error) {
          console.warn('File not saved ' + JSON.stringify(error));
          // TODO: display message
          reject(error);
        }
      };

      reader.onerror = (error): void => {
        reject(error);
      };

      reader.readAsDataURL(fileInput);
    });
  }

  // TODO recover this
  // TODO move to other service ???
  // Used in market... review, ¿work?
  // public async setDriverAssignation(driver: any): Promise<void> {
  //   this.freightCommandService.replaceScheduledJourneyDriver(driver as unknown as ReplaceScheduledJourneyDriverCommand);
  // }

  public async assignVehiclesAndDriver(
    freight: IFreightBasicForCarrier,
    driver: IUser,
    tractor: IVehicle,
    trailer: IVehicle,
  ): Promise<void> {
    // const driverCommand = {
    //   collaboratorId: this.account.company.id,
    //   collaboratorName: this.account.company.name,
    //   driverExclusivePartnerId: 'f1ad8da7-e84d-4d7e-9363-000000000050', // okcargo id
    //   driverId: driver.id,
    //   driverName: driver === null ? null : driver.firstName + ' ' + driver.lastName,
    //   freightId: freight.id,
    //   // journeyId: freight.journeyId,
    // } as ReplaceScheduledJourneyDriverCommand;
    // console.log('assignVehiclesAndDriver() driverCommand', driverCommand);
    // const tractorCommand = {
    //   freightId: freight.id,
    //   // journeyId: freight.journeyId,
    //   vehicleCompanyId: this.account.company.id,
    //   plateNumber: tractor.plate,
    //   vehicleExclusivePartnerId: 'f1ad8da7-e84d-4d7e-9363-000000000050', // okcargo id
    //   vehicleId: tractor.id,
    //   vehicleType: tractor.vehicleType as unknown as VehicleType,
    // } as ReplaceScheduledJourneyVehicleCommand;
    // const trailerCommand = {
    //   freightId: freight.id,
    //   // journeyId: freight.journeyId,
    //   vehicleCompanyId: this.account.company.id,
    //   plateNumber: trailer.plate,
    //   vehicleExclusivePartnerId: 'f1ad8da7-e84d-4d7e-9363-000000000050', // okcargo id
    //   vehicleId: trailer.id,
    //   vehicleType: trailer.vehicleType as unknown as VehicleType,
    // } as ReplaceScheduledJourneyVehicleCommand;
    // await Promise.all([
    //   this.freightCommandService.replaceScheduledJourneyDriver(driverCommand),
    //   this.freightCommandService.replaceScheduledJourneyVehicle(tractorCommand),
    //   this.freightCommandService.replaceScheduledJourneyVehicle(trailerCommand),
    // ]);
  }

  public async sendFastpayRequest(freightId: UUID, discount: number): Promise<void> {
    const command: RequestPayInAdvanceCommand = {
      id: freightId,
      discount: discount,
    };
    this.freightCommandService.requestPayInAdvance(command);
  }

  /// TODO move to service or another
  private getFileFormattedDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // getMonth() returns 0-11
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${year}${month}${day}_${hours}${minutes}`;
  }
}
